import { Button } from '@cian/ui-kit/button';
import { Input } from '@cian/ui-kit/input';
import { IconActionArrowRight16, IconActionSearch16 } from '@cian/ui-kit-design-tokens/icons';
import * as React from 'react';

import * as styles from './DesktopSearchBlockInput.css';
import { dispatchFilterClickEvent } from 'shared/utils/filterClickEvent';
import { trackInputInFocused } from 'shared/containers/DesktopSearchInputContainer/tracking';
import { DesktopSearchFormLayout } from '../DesktopSearchFormLayout/DesktopSearchFormLayout';

interface IDesktopSearchBlockInputProps {
  query: string;
  loading: boolean;
  disabled?: boolean;
  inputRef: React.Ref<HTMLInputElement>;
  onQueryChange(query: string): void;
  onSubmit(isMap: boolean): void;
  onChangeFocus(isFocused: boolean): void;
  isSearchPage: boolean;
}

export const DesktopSearchBlockInput = React.forwardRef<HTMLDivElement, IDesktopSearchBlockInputProps>(
  function DesktopSearchBlockInput(
    { query, loading, disabled, inputRef, onQueryChange, onSubmit, onChangeFocus, isSearchPage },
    ref,
  ) {
    const [isFocused, setIsFocused] = React.useState(false);

    React.useEffect(() => {
      const handlePageShow = (event: PageTransitionEvent) => {
        if (event.persisted) {
          setIsFocused(false);
        }
      };

      window.addEventListener('pageshow', handlePageShow);

      return () => {
        window.removeEventListener('pageshow', handlePageShow);
      };
    }, []);

    const handleInputChange = React.useCallback(
      (_: React.ChangeEvent<HTMLInputElement>, value: string) => onQueryChange(value),
      [onQueryChange],
    );

    const handleFocusChange = React.useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        const newValue = !isFocused;
        const name = e.relatedTarget && (e.relatedTarget as HTMLElement).getAttribute('data-name');

        onChangeFocus(newValue);

        if (loading || (name !== 'search-submit' && name !== 'map-search-submit')) {
          setIsFocused(newValue);

          if (newValue) {
            trackInputInFocused();
          }
        }
      },
      [isFocused, loading, onChangeFocus],
    );

    const handleSubmit = React.useCallback(
      (e: React.FormEvent) => {
        e.preventDefault();
        if (loading) {
          return;
        }

        onSubmit(false);
      },
      [loading, onSubmit],
    );

    const handleMapClick = React.useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        if (loading) {
          return;
        }

        onSubmit(true);
      },
      [loading, onSubmit],
    );

    const handleFiltersClick = React.useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        if (loading) {
          return;
        }

        dispatchFilterClickEvent();
      },
      [loading],
    );

    return (
      <div className={styles['container']} ref={ref}>
        <DesktopSearchFormLayout isSearchPage={isSearchPage} onSubmit={handleSubmit}>
          <IconActionSearch16 color={isSearchPage ? 'icon-main-default' : 'icon-primary-default'} />
          <Input
            onFocus={handleFocusChange}
            onBlur={handleFocusChange}
            inputRef={inputRef}
            placeholder="Купить квартиру с большой кухней рядом с метро"
            size="M"
            value={query}
            onChange={handleInputChange}
            data-testid="desktop-search-input"
          />
          <div className={styles['buttons']}>
            <div className={styles['button']}>
              <Button
                data-name="search-submit"
                data-testid="desktop-search-button"
                size="XS"
                title="Найти"
                theme="fill_primary"
                loading={loading}
                disabled={disabled}
                beforeIcon={<IconActionArrowRight16 color={'icon-inverted-default'} />}
              />
            </div>
            <div className={styles['button-map']}>
              <Button
                data-name="map-search-submit"
                data-testid="desktop-map-search-button"
                size="XS"
                title="Найти на карте"
                theme="fill_secondary"
                disabled={loading || disabled}
                onClick={handleMapClick}
              >
                На карте
              </Button>
            </div>
          </div>
        </DesktopSearchFormLayout>
        {isSearchPage && isFocused && (
          <Button
            data-name="search-submit"
            data-testid="desktop-search-button"
            title="Найти"
            theme="fill_primary"
            loading={loading || disabled}
            onClick={handleFiltersClick}
          >
            Найти
          </Button>
        )}
      </div>
    );
  },
);
